import React, { useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Target,
  Building2,
  Users,
  Settings,
  LogOut,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../context/AuthContext";
import {
  motionStaggerChild,
  motionHoverLift,
} from "../../../lib/design-system";
import { cn } from "../../../lib/utils";

// Mirrors the visual idiom of TeacherDashboard/Components/Sidebar.jsx so the
// admin chrome feels identical to the rest of the app. Items flagged
// comingSoon are visible but disabled — gives admins a sense of the roadmap
// without enabling stubs.

const MAIN_MENU_ITEMS = [
  {
    id: "Programs",
    label: "Programs & Outcomes",
    icon: Target,
    comingSoon: false,
  },
  {
    id: "Departments",
    label: "Departments",
    icon: Building2,
    comingSoon: true,
    estimatedRelease: "Q3 2026",
  },
  {
    id: "Users",
    label: "Users",
    icon: Users,
    comingSoon: true,
    estimatedRelease: "Q3 2026",
  },
];

const ACCOUNT_ITEMS = [
  {
    id: "Settings",
    label: "Settings",
    icon: Settings,
    comingSoon: true,
    estimatedRelease: "Q3 2026",
  },
];

const SectionLabel = React.memo(({ children, isCollapsed, isMobileOpen }) => (
  <AnimatePresence mode="wait">
    {(!isCollapsed || isMobileOpen) && (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="px-4 mb-3 mt-8 text-xs uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500"
      >
        {children}
      </motion.div>
    )}
  </AnimatePresence>
));
SectionLabel.displayName = "SectionLabel";

const NavItem = React.memo(
  ({ item, isActive, isCollapsed, isMobileOpen, onClick, index }) => {
    const Icon = item.icon;
    return (
      <motion.button
        {...motionStaggerChild(index, 0)}
        onClick={onClick}
        whileHover={item.comingSoon ? undefined : motionHoverLift}
        whileTap={item.comingSoon ? undefined : { scale: 0.98 }}
        disabled={item.comingSoon}
        className={cn(
          "relative w-full flex items-center gap-4 px-4 py-3 rounded-sm transition-all duration-200",
          isActive
            ? "bg-accent1 text-white font-semibold shadow-sm"
            : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 font-medium",
          item.comingSoon && "opacity-50 cursor-not-allowed"
        )}
      >
        <Icon className="h-5 w-5 flex-shrink-0" />
        <AnimatePresence>
          {(!isCollapsed || isMobileOpen) && (
            <motion.span
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: "auto" }}
              exit={{ opacity: 0, width: 0 }}
              transition={{ duration: 0.2 }}
              className="whitespace-nowrap text-[15px] flex items-center gap-2"
            >
              {item.label}
              {item.comingSoon && (
                <span className="text-[10px] uppercase tracking-wider font-bold text-gray-400">
                  soon
                </span>
              )}
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>
    );
  }
);
NavItem.displayName = "NavItem";

const AdminSidebar = ({
  activeSection,
  setActiveSection,
  isMobileOpen,
  setIsMobileOpen,
}) => {
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  const handleLogout = useCallback(() => {
    logout();
    navigate("/login");
  }, [logout, navigate]);

  const handleItemClick = useCallback(
    (item) => {
      if (!item.comingSoon) {
        setActiveSection(item.id);
        if (setIsMobileOpen) setIsMobileOpen(false);
      }
    },
    [setActiveSection, setIsMobileOpen]
  );

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={cn(
          "fixed lg:static inset-y-0 left-0 z-50",
          isMobileOpen
            ? "translate-x-0"
            : "-translate-x-full lg:translate-x-0",
          isCollapsed && !isMobileOpen ? "lg:w-20" : "w-64",
          "dark:bg-gray-900 bg-white shadow-md transition-all duration-300 border-r border-gray-200 dark:border-gray-700 flex flex-col justify-between h-screen"
        )}
        onMouseEnter={() => !isMobileOpen && setIsCollapsed(false)}
        onMouseLeave={() => !isMobileOpen && setIsCollapsed(true)}
      >
        {/* Mobile Close Button */}
        {isMobileOpen && (
          <button
            onClick={() => setIsMobileOpen(false)}
            className="absolute top-4 right-4 p-2 rounded-sm hover:bg-gray-100 dark:hover:bg-gray-800 lg:hidden"
          >
            <X className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </button>
        )}

        {/* Top section */}
        <div>
          {/* Logo / brand */}
          <div className="mt-8 mb-2 flex items-center justify-center px-6 gap-2">
            {isCollapsed && !isMobileOpen ? (
              <div className="h-10 w-10 rounded-sm bg-accent1 flex items-center justify-center">
                <span className="font-black text-white text-lg">G</span>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-sm bg-accent1 flex items-center justify-center">
                  <span className="font-black text-white text-lg">A</span>
                </div>
                <div>
                  <div className="font-black text-gray-900 dark:text-white tracking-tight text-lg leading-none">
                    Acaera
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500 mt-1">
                    Admin Console
                  </div>
                </div>
              </div>
            )}
          </div>

          <SectionLabel isCollapsed={isCollapsed} isMobileOpen={isMobileOpen}>
            Workspace
          </SectionLabel>
          <nav className="px-3 flex flex-col gap-1">
            {MAIN_MENU_ITEMS.map((item, i) => (
              <NavItem
                key={item.id}
                item={item}
                isActive={activeSection === item.id}
                isCollapsed={isCollapsed}
                isMobileOpen={isMobileOpen}
                onClick={() => handleItemClick(item)}
                index={i}
              />
            ))}
          </nav>

          <SectionLabel isCollapsed={isCollapsed} isMobileOpen={isMobileOpen}>
            Account
          </SectionLabel>
          <nav className="px-3 flex flex-col gap-1">
            {ACCOUNT_ITEMS.map((item, i) => (
              <NavItem
                key={item.id}
                item={item}
                isActive={activeSection === item.id}
                isCollapsed={isCollapsed}
                isMobileOpen={isMobileOpen}
                onClick={() => handleItemClick(item)}
                index={MAIN_MENU_ITEMS.length + i}
              />
            ))}
          </nav>
        </div>

        {/* Bottom: signed-in card + logout */}
        <div className="px-3 pb-6">
          {user && (!isCollapsed || isMobileOpen) && (
            <div className="mb-3 px-4 py-3 rounded-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
              <div className="text-xs uppercase tracking-wider font-bold text-gray-400">
                Signed in as
              </div>
              <div className="mt-1 text-sm font-bold text-gray-900 dark:text-white truncate">
                {user.name || user.email}
              </div>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-4 px-4 py-3 rounded-sm text-gray-700 dark:text-gray-300 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400 transition-colors font-medium"
          >
            <LogOut className="h-5 w-5 flex-shrink-0" />
            <AnimatePresence>
              {(!isCollapsed || isMobileOpen) && (
                <motion.span
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: "auto" }}
                  exit={{ opacity: 0, width: 0 }}
                  transition={{ duration: 0.2 }}
                  className="whitespace-nowrap text-[15px]"
                >
                  Log out
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </div>
      </div>
    </>
  );
};

export default AdminSidebar;
